import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { Integration } from "@redotech/redo-model/advanced-flow/integrations";
import { TimeframeConditionType } from "@redotech/redo-model/marketing/segments/segment-timeframe";
import { CustomerCharacteristicType } from "@redotech/redo-model/marketing/segments/segment-types";
import {
  ComparisonType,
  ListCompareOperators,
  NumericCompareOperator,
  ProximityCompareOperators,
  ProximityUnit,
  StringCompareOperators,
  TokenCompareOperators,
  WhereConditionDataType,
} from "@redotech/redo-model/segments/segment-where-condition";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInputButton } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex, FlexProps } from "../../../flex";
import { FilterConfig } from "../data-filters/filter-config";
import {
  blockBorderProps,
  SegmentBuilderLocationContext,
  SegmentEditorLocation,
} from "../segment-builder-editor";
import { SegmentBuilderText } from "../segment-builder-text";
import {
  characteristicToIcon,
  characteristicToPostfix,
  segmentCategoryToIcon,
} from "../segment-category";
import {
  CustomerAttributeConditionInProgress,
  WhereConditionInProgress,
} from "../segment-in-progress";
import {
  Characteristic,
  getSegmentDataStructureField,
} from "./customer-characteristics";
import { SegmentCharacteristicSelectModal } from "./segment-characteristic-select-modal";

export const SegmentCharacteristicCondition = memo(
  function SegmentCharacteristicCondition({
    condition,
    setCondition,
    deleteRequested,
    enabledIntegrations,
    hiddenCharacteristics,
  }: {
    condition: CustomerAttributeConditionInProgress;
    setCondition: (condition: CustomerAttributeConditionInProgress) => void;
    deleteRequested: () => void;
    enabledIntegrations?: Record<Integration, boolean>;
    hiddenCharacteristics?: Set<CustomerCharacteristicType>;
  }) {
    const location = useContext(SegmentBuilderLocationContext);

    const containerProps = useMemo<FlexProps>(() => {
      switch (location) {
        case SegmentEditorLocation.SEGMENT_BUILDER:
          return { ...blockBorderProps, p: "md" };
        case SegmentEditorLocation.AUTOMATIONS_PANEL:
          return {};
        default:
          assertNever(location);
      }
    }, [location]);

    return (
      <Flex {...containerProps} align="center" justify="space-between">
        {condition.whereCondition ? (
          <SegmentCharacteristicConditionConfig
            condition={condition}
            deleteRequested={deleteRequested}
            enabledIntegrations={enabledIntegrations}
            hiddenCharacteristics={hiddenCharacteristics}
            setCondition={setCondition}
          />
        ) : (
          <SegmentCharacteristicConditionInit
            characteristicSelected={(characteristic) => {
              setCondition({
                ...condition,
                whereCondition:
                  customerCharacteristicToDefault[characteristic.type],
              });
            }}
            deleteRequested={deleteRequested}
            enabledIntegrations={enabledIntegrations}
            hiddenCharacteristics={hiddenCharacteristics}
          />
        )}
      </Flex>
    );
  },
);

const SegmentCharacteristicConditionInit = memo(
  function SegmentCharacteristicConditionInit({
    characteristicSelected,
    deleteRequested,
    enabledIntegrations,
    hiddenCharacteristics,
  }: {
    characteristicSelected: (characteristic: Characteristic) => void;
    deleteRequested: () => void;
    enabledIntegrations?: Record<Integration, boolean>;
    hiddenCharacteristics?: Set<CustomerCharacteristicType>;
  }) {
    const [characteristicSelectorOpen, setCharacteristicSelectorOpen] =
      useState(true);

    return (
      <>
        <SegmentCharacteristicSelectModal
          characteristicSelected={(characteristic) => {
            characteristicSelected(characteristic);
            setCharacteristicSelectorOpen(false);
          }}
          enabledIntegrations={enabledIntegrations}
          hiddenCharacteristics={hiddenCharacteristics}
          isOpen={characteristicSelectorOpen}
          onModalCloseRequested={() => setCharacteristicSelectorOpen(false)}
        />
        <Flex align="center" gap="md">
          <RedoButton
            hierarchy="secondary"
            IconTrailing={PlusIcon}
            onClick={() => setCharacteristicSelectorOpen(true)}
            text="Select characteristic"
          />
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </>
    );
  },
);

const customerCharacteristicToDefault: Record<
  CustomerCharacteristicType,
  WhereConditionInProgress<CustomerCharacteristicType>
> = {
  [CustomerCharacteristicType.COUNTRY]: {
    dimension: CustomerCharacteristicType.COUNTRY,
    type: WhereConditionDataType.TOKEN,
    comparison: {
      type: ComparisonType.TOKEN,
      operator: TokenCompareOperators.ANY,
      values: [],
    },
  },
  [CustomerCharacteristicType.STATE_PROVINCE]: {
    dimension: CustomerCharacteristicType.STATE_PROVINCE,
    type: WhereConditionDataType.TOKEN_HIERARCHY,
    comparison: {
      type: ComparisonType.TOKEN,
      operator: TokenCompareOperators.ANY,
      prerequisiteValues: [],
      values: [],
    },
  },
  [CustomerCharacteristicType.CITY]: {
    dimension: CustomerCharacteristicType.CITY,
    type: WhereConditionDataType.TOKEN_HIERARCHY,
    comparison: {
      type: ComparisonType.TOKEN,
      operator: TokenCompareOperators.ANY,
      prerequisiteValues: [],
      values: [],
    },
  },
  [CustomerCharacteristicType.PROXIMITY_TO_CITY]: {
    dimension: CustomerCharacteristicType.PROXIMITY_TO_CITY,
    type: WhereConditionDataType.CITY_PROXIMITY,
    comparison: {
      type: ComparisonType.PROXIMITY,
      operator: ProximityCompareOperators.WITHIN,
      prerequisiteValues: [],
      value: "",
      options: { radius: 25, units: ProximityUnit.MILES },
    },
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL]: {
    dimension: CustomerCharacteristicType.SUBSCRIBED_TO_EMAIL,
    type: WhereConditionDataType.BOOLEAN,
    comparison: { type: ComparisonType.BOOLEAN, value: true },
  },
  [CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING]: {
    dimension: CustomerCharacteristicType.CAN_RECEIVE_EMAIL_MARKETING,
    type: WhereConditionDataType.BOOLEAN,
    comparison: { type: ComparisonType.BOOLEAN, value: true },
  },
  [CustomerCharacteristicType.SUBSCRIBED_TO_SMS]: {
    dimension: CustomerCharacteristicType.SUBSCRIBED_TO_SMS,
    type: WhereConditionDataType.BOOLEAN,
    comparison: { type: ComparisonType.BOOLEAN, value: true },
  },
  [CustomerCharacteristicType.CREATED_TIME]: {
    dimension: CustomerCharacteristicType.CREATED_TIME,
    type: WhereConditionDataType.DATE_FULL,
    comparison: { type: TimeframeConditionType.BEFORE },
  },
  [CustomerCharacteristicType.CUSTOMER_TAGS]: {
    dimension: CustomerCharacteristicType.CUSTOMER_TAGS,
    type: WhereConditionDataType.TOKEN_LIST,
    comparison: {
      type: ComparisonType.LIST,
      operator: ListCompareOperators.ANY,
      values: [],
    },
  },
  [CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP]: {
    dimension: CustomerCharacteristicType.STATIC_SEGMENT_MEMBERSHIP,
    type: WhereConditionDataType.TOKEN_LIST,
    comparison: {
      type: ComparisonType.LIST,
      operator: ListCompareOperators.ANY,
      values: [],
    },
  },
  [CustomerCharacteristicType.CUSTOM_FIELDS]: {
    dimension: CustomerCharacteristicType.CUSTOM_FIELDS,
    type: WhereConditionDataType.CUSTOM_FIELD,
    declarationId: "", // This will need to be selected in the UI
    comparison: {
      type: ComparisonType.TOKEN,
      operator: TokenCompareOperators.ANY,
      values: [],
    },
  },
  [CustomerCharacteristicType.RETURN_RATE_VALUE]: {
    dimension: CustomerCharacteristicType.RETURN_RATE_VALUE,
    type: WhereConditionDataType.NUMERIC,
    comparison: {
      type: ComparisonType.NUMERIC,
      operator: NumericCompareOperator.LT,
      value: undefined,
    },
  },
  [CustomerCharacteristicType.RETURN_RATE_COUNT]: {
    dimension: CustomerCharacteristicType.RETURN_RATE_COUNT,
    type: WhereConditionDataType.NUMERIC,
    comparison: {
      type: ComparisonType.NUMERIC,
      operator: NumericCompareOperator.LT,
      value: undefined,
    },
  },
  [CustomerCharacteristicType.BIRTHDAY]: {
    dimension: CustomerCharacteristicType.BIRTHDAY,
    type: WhereConditionDataType.DATE_ANNUAL,
    comparison: { type: TimeframeConditionType.BEFORE },
  },
  [CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE]: {
    dimension: CustomerCharacteristicType.PHONE_NUMBER_AREA_CODE,
    type: WhereConditionDataType.TOKEN,
    comparison: {
      type: ComparisonType.TOKEN,
      operator: TokenCompareOperators.ANY,
      values: [],
    },
  },
  [CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION]: {
    dimension: CustomerCharacteristicType.ENROLLED_IN_LOYALTY_LION,
    type: WhereConditionDataType.BOOLEAN,
    comparison: { type: ComparisonType.BOOLEAN, value: true },
  },
  [CustomerCharacteristicType.EMAIL_ADDRESS]: {
    dimension: CustomerCharacteristicType.EMAIL_ADDRESS,
    type: WhereConditionDataType.STRING,
    comparison: {
      type: ComparisonType.STRING,
      operator: StringCompareOperators.CONTAINS,
      value: "",
    },
  },
  [CustomerCharacteristicType.CUSTOMER_NAME]: {
    dimension: CustomerCharacteristicType.CUSTOMER_NAME,
    type: WhereConditionDataType.STRING,
    comparison: {
      type: ComparisonType.STRING,
      operator: StringCompareOperators.CONTAINS,
      value: "",
    },
  },
};

const SegmentCharacteristicConditionConfig = memo(
  function SegmentCharacteristicConditionConfig({
    condition,
    setCondition,
    deleteRequested,
    enabledIntegrations,
    hiddenCharacteristics,
  }: {
    condition: CustomerAttributeConditionInProgress;
    setCondition: (condition: CustomerAttributeConditionInProgress) => void;
    deleteRequested: () => void;
    enabledIntegrations?: Record<Integration, boolean>;
    hiddenCharacteristics?: Set<CustomerCharacteristicType>;
  }) {
    return (
      <>
        <Flex align="center" gap="md" wrap="wrap">
          <CharacteristicSelector
            enabledIntegrations={enabledIntegrations}
            hiddenCharacteristics={hiddenCharacteristics}
            selectedCharacteristic={getSegmentDataStructureField(
              "customer-characteristic",
              condition.whereCondition!.dimension,
            )}
            setSelectedCharacteristic={(characteristic) => {
              if (characteristic.type === condition.whereCondition!.dimension) {
                return;
              }
              setCondition({
                ...condition,
                whereCondition:
                  customerCharacteristicToDefault[characteristic.type],
              });
            }}
          />
          <FilterConfig
            condition={condition.whereCondition!}
            dataStructure="customer-characteristic"
            setCondition={(newWhereCondition) =>
              setCondition({ ...condition, whereCondition: newWhereCondition })
            }
          />
          <CharacteristicPostfix
            characteristic={getSegmentDataStructureField(
              "customer-characteristic",
              condition.whereCondition!.dimension,
            )}
          />
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </>
    );
  },
);

const CharacteristicPostfix = memo(function CharacteristicPostfix({
  characteristic,
}: {
  characteristic: Characteristic | undefined;
}) {
  const postfix =
    characteristic && characteristicToPostfix[characteristic.type];

  if (!postfix) {
    return null;
  }

  return <SegmentBuilderText>{postfix}</SegmentBuilderText>;
});

const CharacteristicSelector = memo(function CharacteristicSelector({
  selectedCharacteristic,
  setSelectedCharacteristic,
  enabledIntegrations,
  hiddenCharacteristics,
}: {
  selectedCharacteristic: Characteristic | undefined;
  setSelectedCharacteristic: (characteristic: Characteristic) => void;
  enabledIntegrations?: Record<Integration, boolean>;
  hiddenCharacteristics?: Set<CustomerCharacteristicType>;
}) {
  const [characteristicSelectorOpen, setCharacteristicSelectorOpen] =
    useState(false);

  const item: RedoListItem<Characteristic> | undefined = useMemo(() => {
    if (!selectedCharacteristic) {
      return undefined;
    }

    const Icon =
      characteristicToIcon[selectedCharacteristic.type] ||
      segmentCategoryToIcon[selectedCharacteristic.category];

    return {
      id: selectedCharacteristic.name,
      value: selectedCharacteristic,
      type: "text",
      text: selectedCharacteristic.name,
      leadingItem: { type: "icon", Icon },
    };
  }, [selectedCharacteristic]);

  return (
    <>
      <SegmentCharacteristicSelectModal
        characteristicSelected={(characteristic) => {
          setSelectedCharacteristic(characteristic);
          setCharacteristicSelectorOpen(false);
        }}
        enabledIntegrations={enabledIntegrations}
        hiddenCharacteristics={hiddenCharacteristics}
        initialSelectedCharacteristic={selectedCharacteristic}
        isOpen={characteristicSelectorOpen}
        onModalCloseRequested={() => setCharacteristicSelectorOpen(false)}
      />
      <Flex>
        <RedoSingleSelectDropdownInputButton<Characteristic>
          isDropdownOpen={false}
          onClick={() => setCharacteristicSelectorOpen(true)}
          selectedItem={item}
          size="xs"
        />
      </Flex>
    </>
  );
});
